/*
 * Small brand-tinted line illustrations for empty states. They draw with the
 * design tokens, so they follow light and dark mode automatically.
 */
export type IllustrationKind = "tasks" | "today" | "calendar" | "search" | "inbox" | "archive" | "trash" | "habits" | "activity" | "filters" | "focus" | "bell" | "label";

const S = {
  card: "var(--color-surface-raised)",
  line: "var(--color-border-strong)",
  soft: "var(--color-brand-soft)",
  brand: "var(--color-brand)",
  faint: "var(--color-surface-sunken)",
};

function Base({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <svg viewBox="0 0 160 112" className={className} aria-hidden fill="none" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="80" cy="100" rx="56" ry="6" fill={S.faint} />
      {children}
    </svg>
  );
}

export function Illustration({ kind, className }: { kind: IllustrationKind; className?: string }) {
  switch (kind) {
    case "today":
      return (
        <Base className={className}>
          <circle cx="80" cy="50" r="30" fill={S.soft} />
          <circle cx="80" cy="50" r="16" fill={S.card} stroke={S.brand} strokeWidth="2.5" />
          <path d="M73 50l5 5 9-10" stroke={S.brand} strokeWidth="2.5" />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
            const r = (a * Math.PI) / 180;
            return <path key={a} d={`M${80 + Math.cos(r) * 36} ${50 + Math.sin(r) * 36}l${Math.cos(r) * 6} ${Math.sin(r) * 6}`} stroke={S.brand} strokeWidth="2.5" opacity=".55" />;
          })}
        </Base>
      );
    case "calendar":
      return (
        <Base className={className}>
          <rect x="44" y="20" width="72" height="66" rx="10" fill={S.card} stroke={S.line} strokeWidth="2" />
          <path d="M44 38h72" stroke={S.line} strokeWidth="2" />
          <path d="M60 14v12M100 14v12" stroke={S.line} strokeWidth="2.5" />
          {[0, 1, 2, 3].map((c) => [0, 1, 2].map((r) => (
            <rect key={`${c}${r}`} x={54 + c * 14} y={46 + r * 12} width="8" height="6" rx="2" fill={c === 2 && r === 1 ? S.brand : S.faint} />
          )))}
        </Base>
      );
    case "search":
      return (
        <Base className={className}>
          <circle cx="72" cy="48" r="26" fill={S.soft} />
          <circle cx="72" cy="48" r="18" fill={S.card} stroke={S.brand} strokeWidth="3" />
          <path d="M86 62l18 18" stroke={S.brand} strokeWidth="5" />
          <path d="M64 44h16M64 52h10" stroke={S.line} strokeWidth="2.5" />
        </Base>
      );
    case "inbox":
      return (
        <Base className={className}>
          <path d="M46 56l10-30h48l10 30v24a6 6 0 01-6 6H52a6 6 0 01-6-6z" fill={S.card} stroke={S.line} strokeWidth="2" />
          <path d="M46 56h20l4 8h20l4-8h20" stroke={S.line} strokeWidth="2" />
          <circle cx="80" cy="40" r="9" fill={S.soft} />
          <path d="M76 40l3 3 5-6" stroke={S.brand} strokeWidth="2.5" />
        </Base>
      );
    case "archive":
    case "trash":
      return (
        <Base className={className}>
          <rect x="50" y="34" width="60" height="52" rx="6" fill={S.card} stroke={S.line} strokeWidth="2" />
          <rect x="44" y="24" width="72" height="14" rx="4" fill={S.soft} stroke={S.brand} strokeWidth="2" />
          {kind === "trash" ? <path d="M70 48v26M80 48v26M90 48v26" stroke={S.line} strokeWidth="2.5" /> : <path d="M72 52h16" stroke={S.brand} strokeWidth="3" />}
        </Base>
      );
    case "habits":
      return (
        <Base className={className}>
          {[0, 1, 2, 3, 4].map((i) => (
            <circle key={i} cx={44 + i * 18} cy="54" r="7" fill={i < 3 ? S.brand : S.card} stroke={i < 3 ? S.brand : S.line} strokeWidth="2" opacity={i < 3 ? 0.35 + i * 0.25 : 1} />
          ))}
          <path d="M80 22c6 6 9 11 9 16a9 9 0 01-18 0c0-3 1-5 3-7 0 3 2 5 4 5-1-5 0-9 2-14z" fill={S.soft} stroke={S.brand} strokeWidth="2" />
        </Base>
      );
    case "activity":
    case "bell":
      return (
        <Base className={className}>
          <circle cx="80" cy="50" r="32" fill={S.soft} />
          {kind === "bell"
            ? <path d="M68 62V48a12 12 0 0124 0v14l4 4H64zM76 70a4 4 0 008 0" fill={S.card} stroke={S.brand} strokeWidth="2.5" />
            : <><circle cx="80" cy="50" r="18" fill={S.card} stroke={S.brand} strokeWidth="2.5" /><path d="M80 40v10l7 5" stroke={S.brand} strokeWidth="2.5" /></>}
        </Base>
      );
    case "focus":
      return (
        <Base className={className}>
          <circle cx="80" cy="52" r="30" fill={S.card} stroke={S.line} strokeWidth="3" />
          <path d="M80 22a30 30 0 0130 30" stroke={S.brand} strokeWidth="5" />
          <path d="M80 36v16l9 6" stroke={S.brand} strokeWidth="2.5" />
        </Base>
      );
    case "filters":
    case "label":
      return (
        <Base className={className}>
          <path d="M52 30h34l24 24-24 24-34-34z" transform="translate(0 6)" fill={S.card} stroke={S.line} strokeWidth="2" />
          <circle cx="66" cy="44" r="5" fill={S.soft} stroke={S.brand} strokeWidth="2" />
          <path d="M84 30h10l24 24-14 14" stroke={S.brand} strokeWidth="2" opacity=".5" />
        </Base>
      );
    case "tasks":
    default:
      return (
        <Base className={className}>
          <rect x="42" y="18" width="76" height="72" rx="10" fill={S.card} stroke={S.line} strokeWidth="2" />
          {[0, 1, 2].map((i) => (
            <g key={i}>
              <circle cx="58" cy={38 + i * 18} r="6" fill={i === 0 ? S.brand : "none"} stroke={i === 0 ? S.brand : S.line} strokeWidth="2" />
              {i === 0 && <path d="M55 38l2 2 4-4" stroke="#fff" strokeWidth="2" />}
              <path d={`M70 ${38 + i * 18}h${i === 0 ? 34 : 28 - i * 4}`} stroke={i === 0 ? S.soft : S.line} strokeWidth="4" />
            </g>
          ))}
        </Base>
      );
  }
}
